import React, { useEffect, useState } from 'react';
import { DOMAINS } from '../../../shared/constants/domains';
import { sessionSummary } from '../engine/stats';

const CONFETTI_COLORS = ['#4A90D9', '#7B68EE', '#2ECC71', '#F39C12', '#E74C3C', '#1ABC9C'];

function Confetti({ show }) {
  const [pieces, setPieces] = useState([]);

  useEffect(() => {
    if (!show) return;
    const generated = Array.from({ length: 40 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 1.2}s`,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 6 + Math.random() * 8,
      shape: Math.random() > 0.5 ? 'circle' : 'square',
    }));
    setPieces(generated);
  }, [show]);

  if (!show || pieces.length === 0) return null;

  return (
    <div className="confetti-container">
      {pieces.map(p => (
        <div
          key={p.id}
          className="confetti-piece"
          style={{
            left: p.left,
            animationDelay: p.delay,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
          }}
        />
      ))}
    </div>
  );
}

function AccuracyRing({ value, size = 140, stroke = 10 }) {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const color = value >= 80 ? '#2ECC71' : value >= 60 ? '#F39C12' : '#E74C3C';
  const glowClass = value >= 80 ? 'text-glow-green' : value >= 60 ? 'text-glow-yellow' : 'text-glow-red';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="#374151" strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="progress-ring-circle"
          style={{ filter: `drop-shadow(0 0 8px ${color}80)` }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-4xl font-bold ${glowClass}`} style={{ color }}>
          {value}%
        </span>
        <span className="text-xs text-gray-500 mt-0.5">accuracy</span>
      </div>
    </div>
  );
}

/**
 * Post-session summary showing accuracy, domain breakdown, and streak.
 */
export default function SessionSummary({ reviews, duration, mode, onDone, graduatedCount, totalServed }) {
  const summary = sessionSummary(reviews);
  const accuracy = Math.round(summary.accuracy * 100);

  const modeLabel = {
    review: 'Review Session',
    cram: 'Cram Session',
    weak: 'Weak Spots Session',
  }[mode] || 'Session';

  return (
    <div className="flex flex-col items-center px-4 py-8 max-w-lg mx-auto page-enter">
      <Confetti show={accuracy >= 70} />

      <h2 className="text-2xl font-bold text-white mb-6">{modeLabel} Complete</h2>

      {/* Accuracy ring */}
      <div className="mb-8 animate-scale-in">
        <AccuracyRing value={accuracy} />
      </div>

      {/* Stats grid */}
      <div className={`grid ${graduatedCount != null ? 'grid-cols-4' : 'grid-cols-3'} gap-3 w-full mb-8`}>
        <StatBox label="Reviewed" value={summary.cards_reviewed} />
        <StatBox label="Correct" value={summary.cards_correct} />
        {graduatedCount != null && (
          <StatBox label="Mastered" value={`${graduatedCount}/${totalServed}`} />
        )}
        <StatBox label="Best Streak" value={`\u{1F525} ${summary.streak_max}`} />
      </div>

      {/* Duration */}
      <p className="text-gray-500 text-sm mb-8">
        Completed in {Math.floor(duration / 60)}m {duration % 60}s
      </p>

      {/* Domain breakdown */}
      {Object.keys(summary.domain_breakdown).length > 0 && (
        <div className="w-full mb-8">
          <h3 className="text-sm font-medium text-gray-400 mb-3 uppercase tracking-wider">By Domain</h3>
          <div className="space-y-2.5">
            {DOMAINS.filter(d => summary.domain_breakdown[d.id]).map(domain => {
              const db = summary.domain_breakdown[domain.id];
              const pct = db.reviewed > 0 ? Math.round((db.correct / db.reviewed) * 100) : 0;
              return (
                <div key={domain.id} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: domain.color }} />
                  <span className="text-sm text-gray-300 w-32 truncate">{domain.name}</span>
                  <div className="flex-1 bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-2.5 rounded-full transition-all duration-500"
                      style={{
                        width: `${pct}%`,
                        backgroundColor: pct >= 80 ? '#2ECC71' : pct >= 60 ? '#F39C12' : '#E74C3C',
                        boxShadow: `0 0 8px ${pct >= 80 ? 'rgba(46,204,113,0.3)' : pct >= 60 ? 'rgba(243,156,18,0.3)' : 'rgba(231,76,60,0.3)'}`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 w-16 text-right font-mono">
                    {db.correct}/{db.reviewed}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={onDone}
        className="px-10 py-3.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl
          hover:from-blue-500 hover:to-blue-400 font-semibold glow-blue transition-all duration-200 active:scale-95"
      >
        Back to Dashboard
      </button>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="card-gradient p-4 text-center">
      <div className="text-xl font-bold text-white">{value}</div>
      <div className="text-[11px] text-gray-400 mt-1 uppercase tracking-wider">{label}</div>
    </div>
  );
}
